import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { formatUGX, methodLabel } from '../mock';
import { ShoppingCart, Receipt, Package, TrendingDown, ArrowRight, AlertTriangle, Loader2 } from 'lucide-react';

const isToday = (iso) => iso && new Date(iso).toDateString() === new Date().toDateString();

const Tile = ({ label, value, Icon, tone, onClick, sub }) => (
  <button onClick={onClick} className="text-left bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:border-blue-200 transition-all">
    <div className="flex items-start justify-between">
      <span className="text-[13px] font-semibold tracking-wider text-slate-500 uppercase">{label}</span>
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}><Icon size={20} /></div>
    </div>
    <div className="font-display font-extrabold text-3xl text-slate-900 mt-3">{value}</div>
    <div className="text-slate-400 text-sm mt-2 flex items-center gap-1">{sub} <ArrowRight size={13} /></div>
  </button>
);

const DashboardHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCashier = user?.role === 'cashier';
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/products'), api.get('/sales')])
      .then(([p, s]) => { setProducts(p.data); setSales(s.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const todaySales = sales.filter((s) => s.status === 'COMPLETED' && isToday(s.created_at));
  const salesToday = todaySales.reduce((sum, s) => sum + s.total, 0);
  const itemsSold = todaySales.reduce((sum, s) => sum + s.lines.reduce((a, l) => a + l.quantity, 0), 0);
  const lowStock = products.filter((p) => p.low_stock && p.available > 0);
  const outStock = products.filter((p) => p.available <= 0);
  const nearExpiry = products.filter((p) => p.near_expiry);

  if (loading) return <div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading dashboard…</div>;

  return (
    <div>
      <h1 className="font-display font-extrabold text-4xl text-slate-900">Welcome back, {user?.name?.split(' ')[0]}</h1>
      <p className="text-slate-500 mt-2 text-[15px]">Here's how your pharmacy is doing today.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
        <Tile label="Sales today" value={formatUGX(salesToday)} sub="Open POS" Icon={ShoppingCart} tone="bg-green-50 text-green-600" onClick={() => navigate('/app/pos')} />
        <Tile label="Transactions" value={todaySales.length} sub={isCashier ? 'Open POS' : 'View reports'} Icon={Receipt} tone="bg-blue-50 text-blue-600" onClick={() => navigate(isCashier ? '/app/pos' : '/app/reports')} />
        <Tile label="Items sold today" value={itemsSold} sub="Catalog" Icon={Package} tone="bg-indigo-50 text-indigo-600" onClick={() => navigate('/app/catalog')} />
        <Tile label="Out of stock" value={outStock.length} sub="Restock now" Icon={TrendingDown} tone="bg-red-50 text-red-600" onClick={() => navigate('/app/inventory')} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-xl text-slate-900">Recent sales</h3>
            <button onClick={() => navigate('/app/pos')} className="text-blue-600 font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all">New sale <ArrowRight size={15} /></button>
          </div>
          <div className="mt-4 divide-y divide-slate-100">
            {sales.slice(0, 6).map((s) => (
              <div key={s.id} className="flex items-center justify-between py-3">
                <div>
                  <span className="font-medium text-slate-800">{s.sale_number}</span>
                  <span className="text-slate-400 text-sm ml-2">{s.customer} · {methodLabel(s.payment_method)}{s.status === 'VOIDED' ? ' · VOIDED' : ''}</span>
                </div>
                <div className="text-right">
                  <div className={`font-semibold ${s.status === 'VOIDED' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{formatUGX(s.total)}</div>
                  <div className="text-slate-400 text-xs">{s.created_at ? new Date(s.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}</div>
                </div>
              </div>
            ))}
            {sales.length === 0 && <div className="text-slate-400 text-sm py-6 text-center">No sales yet. Head to the POS to make your first sale.</div>}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-xl text-slate-900 flex items-center gap-2"><AlertTriangle size={20} className="text-amber-500" /> Stock alerts</h3>
          <div className="mt-4">
            <div className="text-slate-500 text-sm font-medium mb-2">Low stock ({lowStock.length})</div>
            <div className="space-y-2">
              {lowStock.slice(0, 3).map((p) => (<div key={p.id} className="flex justify-between text-sm"><span className="text-slate-700">{p.name}</span><span className="text-amber-600 font-medium">{p.available} left</span></div>))}
              {lowStock.length === 0 && <div className="text-slate-400 text-sm">All good.</div>}
            </div>
            <div className="text-slate-500 text-sm font-medium mt-5 mb-2">Near expiry ({nearExpiry.length})</div>
            <div className="space-y-2">
              {nearExpiry.slice(0, 3).map((p) => (<div key={p.id} className="flex justify-between text-sm"><span className="text-slate-700">{p.name}</span><span className="text-red-600 font-medium">{p.earliest_expiry}</span></div>))}
              {nearExpiry.length === 0 && <div className="text-slate-400 text-sm">Nothing expiring soon.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
